import * as R from 'ramda';
import React, {useState} from 'react';

import {translate} from 'iss/strings';
import {LAST_DAY_OF_MONTH, addActivationParty, archiveActivation, restoreActivation,
  editLocation, editRecurrence, removeActivationParty, subscribeToActivation} from 'iss/service/EvaluationActivationService';
import {createEvent, submitSelfMonitoring} from 'iss/service/EvaluationEventService';
import {getCurrentParty} from 'iss/service/PartyService';
import {isCompanyEmployee} from 'iss/service/PartyService';
import styles from 'iss/styles/EvaluationActivationEditor.styl';
import dates from 'iss/utils/dates';
import {Button, CancelButton, EditButton, SubmitButton} from 'iss/widgets/Buttons';
import {translateEvaluationType, translateEventAction, translateEventState, translatePartyActivationType, translateRecurrenceType, translateWeekday} from 'iss/widgets/domainTranslations';
import {FlexRow} from 'iss/widgets/Flex';
import {DatePicker, FormWithState} from 'iss/widgets/Forms';
import {WarningBox} from 'iss/widgets/MessageBox';
import {RestResultMessage} from 'iss/widgets/MessageBox';
import {ActivationPartySelector, PartyActivationTypeSelector} from 'iss/widgets/Selectors';
import {ContractLocationEditor} from 'iss/widgets/ContractLocationEditor';
import {RecurrenceDatesSelector, toActivationRecurrenceDates} from 'iss/widgets/activation/DateSelectorSections';
import {ModalForm} from 'iss/widgets/batchOps/ModalForm';
import Modal from 'iss/widgets/bootstrap/Modal';
import {formatParty} from 'iss/widgets/format/Party';
import validators from 'iss/widgets/form/validation/validators';
import {DownloadPdfButton} from '../widgets/DownloadBlobButton';
import {partyActivationTypes} from 'iss/service/ActivationDataService';
import { SortColumnHeader, SortOrderEnum } from '../widgets/SortColumnHeader';
import { eventStates } from '../service/EvaluationEventService';

const EvaluationActivationEditor = subscribeToActivation(function EvaluationActivationEditor({activation, reloadSubscription}) {
  const {id, isActive, events, evaluationParties, location} = activation;
  return <div>
    <h1>{translate('activationEditor.title')}</h1>
    <ActivationInfo activation={activation} />
    {isActive && isCompanyEmployee() &&  <ArchiveActivationButton activationId={activation.id} onChanged={reloadSubscription} />}
    {!isActive && isCompanyEmployee() &&  <RestoreActivationButton activationId={activation.id} onChanged={reloadSubscription} />}
    {isCompanyEmployee() && <EditLocationButton activationId={id} location={location} onChanged={reloadSubscription} />}
    <ActivatedParties activationId={id} isActive={isActive} parties={evaluationParties} onChanged={reloadSubscription} />
    <ActivationRecurrences activation={activation} />
    <EditRecurrenceButton activation={activation} activationId={activation.id} onChanged={reloadSubscription} />
    <ActivationEventList isActive={isActive} events={events} onChanged={reloadSubscription} />
    {isActive ? <CreateEventForm activationId={id} onChanged={reloadSubscription} /> : null}
  </div>;
}, () => <h1>{translate('activationEditor.activationNotFound')}</h1>);

function ActivationInfo({activation}) {
  const {isActive, contract, evaluationForm, serviceType, evaluationType, location, hideLogoOnReport, sendPdfReport} = activation;
  return <section>
    <h2>{translate('activationEditor.contractInfo')}</h2>
    <table id="activated-evaluation-info" className="table table-striped">
      <tbody>
        <tr id="evaluation-activation-state">
          <th>{translate('activationEditor.activationState')}</th>
          <td>
            {isActive ?
                translate('activationEditor.active') :
                translate('activationEditor.archived')
            }
          </td>
        </tr>
        <tr id="evaluation-site-name">
          <th>{translate('activationEditor.companyName')}</th>
          <td>{contract.site.name}</td>
        </tr>
        <tr id="evaluation-site-address">
          <th>{translate('activationEditor.address')}</th>
          <td>{contract.site.streetAddress} {contract.site.city} {contract.site.postCode}</td>
        </tr>
        {location &&
          <tr id="evaluation-location-name">
            <th>{translate('activationEditor.locationName')}</th>
            <td>{location.name} {location.code && "("+location.code+")"}</td>
          </tr>
        }
        {location && location.address &&
          <tr id="evaluation-location-address">
            <th>{translate('activationEditor.locationAddress')}</th>
            <td>{location.address} {location.city}</td>
          </tr>
        }
        <tr id="evaluation-hide-logo">
          <th>{translate('activationEditor.hideLogoOnReport')}</th>
          <td>{translate('activationEditor.hide.' + hideLogoOnReport)}</td>
        </tr>
        <tr id="evaluation-send-pdf-report">
          <th>{translate('activationEditor.sendPdfReport')}</th>
          <td>{translate('activationEditor.sendReports.' + sendPdfReport)}</td>
        </tr>
        <tr id="evaluation-form-name">
          <th>{translate('activationEditor.formName')}</th>
          <td>{evaluationForm.name}</td>
        </tr>
        <tr id="evaluation-service-name">
          <th>{translate('activationEditor.service')}</th>
          <td>{serviceType.name}</td>
        </tr>
        <tr id="evaluation-type">
          <th>{translate('domain.activatedEvaluation.evaluationType')}</th>
          <td>{translateEvaluationType(evaluationType)}</td>
        </tr>
      </tbody>
    </table>
  </section>;
}

class EditLocationButton extends React.Component {
  state = {
    showModal: false,
    backendError: null,
  };
  toggleModal = () => {
    this.setState(R.evolve({showModal: R.not}));
  };
  editLocation = (location) => {
    const {activationId, onChanged} = this.props;
    editLocation(activationId, location)
      .subscribe(
        () => {
          this.toggleModal();
          onChanged();
        }, (errorResponse) => this.setState({backendError: errorResponse})
      );
  };
  render() {
    const {location} = this.props;
    const title = translate(location ? 'activationEditor.editLocation' : 'activationEditor.addLocation');
    const deleteButton = location &&
      <Button id="delete-location" buttonLook="danger" text={translate('activationEditor.deleteLocation')} onClick={() => this.editLocation(null)} />;

    return <React.Fragment>
      <Button onClick={this.toggleModal} id="edit-location">
        {title}
      </Button>
      <Modal title={title} visible={this.state.showModal} size="medium" onClose={this.toggleModal}>
        <ModalForm proceedText={translate(location ? 'common.edit' : 'common.save')}
          additionalButtons={deleteButton}
          key={location}
          initialValues={location}
          onSubmit={this.editLocation}
          onCancel={this.toggleModal}>
          <ContractLocationEditor id="location" />
          <RestResultMessage id="backendError" result={this.state.backendError} />
        </ModalForm>
      </Modal>
    </React.Fragment>;
  }
}

class ArchiveActivationButton extends React.Component {
  state = {
    showModal: false,
    backendError: null,
  };
  toggleModal = () => {
    this.setState(R.evolve({showModal: R.not}));
  };
  archiveActivation = () => {
    const {activationId, onChanged} = this.props;
    archiveActivation(activationId)
      .subscribe(
        () => {
          this.toggleModal();
          onChanged();
        }, (errorResponse) => this.setState({backendError: errorResponse})
      );
  };
  render() {
    return <React.Fragment>
      <Button buttonLook="danger" onClick={this.toggleModal} id="archive-activation" className={styles.archive}>
        {translate('activationEditor.archive')}
      </Button>
      <Modal title={translate('activationEditor.doYouWantToArchive')} visible={this.state.showModal} size="medium" onClose={this.toggleModal}>
        {{
          body: <p>{translate('activationEditor.archivingIsPermanentAndBad')}</p>,
          footer: <React.Fragment>
            <CancelButton onClick={this.toggleModal} id="cancel-archive" />
            <Button buttonLook="danger" onClick={this.archiveActivation} id="confirm-archive">
              {translate('activationEditor.archive')}
            </Button>
            <RestResultMessage id="createEventError" result={this.state.backendError} />
          </React.Fragment>
        }}
      </Modal>
    </React.Fragment>;
  }
}

class RestoreActivationButton extends React.Component {
  state = {
    showModal: false,
    backendError: null,
  };
  toggleModal = () => {
    this.setState(R.evolve({showModal: R.not}));
  };
  restoreActivation = () => {
    const {activationId, onChanged} = this.props;
    restoreActivation(activationId)
      .subscribe(
        () => {
          this.toggleModal();
          onChanged();
        }, (errorResponse) => this.setState({backendError: errorResponse})
      );
  };
  render() {
    return <React.Fragment>
      <Button buttonLook="primary" onClick={this.toggleModal} id="restore-activation" className={styles.archive}>
        {translate('activationEditor.restore')}
      </Button>
      <Modal title={translate('activationEditor.restoreTitle')} visible={this.state.showModal} size="medium" onClose={this.toggleModal}>
        {{
          body: <p>{translate('activationEditor.restoringConfirmation')}</p>,
          footer: <React.Fragment>
            <CancelButton onClick={this.toggleModal} id="cancel-restore" />
            <Button buttonLook="danger" onClick={this.restoreActivation} id="confirm-restore">
              {translate('activationEditor.restore')}
            </Button>
            <RestResultMessage id="createEventError" result={this.state.backendError} />
          </React.Fragment>
        }}
      </Modal>
    </React.Fragment>;
  }
}

function ActivationRecurrences({activation}) {
  const {recurrenceType, recurrenceDates} = activation;
  return <section>
    <h2>{translate('activationEditor.recurrenceDetails')}</h2>
    <table id="recurrence-dates-table" className="table table-striped">
      <tbody>
        <tr id="recurrence-type">
          <th>{translate('activationEditor.recurrenceType')}</th>
          <td>{translateRecurrenceType(recurrenceType)}</td>
        </tr>
        <tr id="recurrence-days">
          <th>{translate('activationEditor.recurrenceDays')}</th>
          <RecurrenceDates type={recurrenceType} dates={recurrenceDates} />
        </tr>
      </tbody>
    </table>
  </section>;
}

class EditRecurrenceButton extends React.Component {
  state = {
    showModal: false,
    backendError: null,
  };
  toggleModal = () => {
    this.setState(R.evolve({showModal: R.not}));
  };
  editRecurrence = ({recurrenceType, recurrenceDates}) => {
    const {activationId, onChanged} = this.props;
    const dates = toActivationRecurrenceDates({recurrenceType, recurrenceDates});
    const recurrenceModifyCommand = {
      recurrenceType,
      'recurrenceDates': dates,
    };

    editRecurrence(activationId, recurrenceModifyCommand)
      .subscribe(
        () => {
          this.toggleModal();
            onChanged();
        }, (errorResponse) => this.setState({backendError: errorResponse})
      );
  };
  render() {
    const {recurrenceType, recurrenceDates} = this.props.activation;
    const title = translate('activationEditor.editRecurrence');

    return <React.Fragment>
      <Button onClick={this.toggleModal} id="edit-recurrence">
        {title}
      </Button>
      <Modal title={title} visible={this.state.showModal} size="medium" onClose={this.toggleModal}>
        <ModalForm proceedText={translate('common.save')}
          key={{recurrenceType, recurrenceDates}}
          initialValues={{recurrenceType, recurrenceDates}}
          onSubmit={this.editRecurrence}
          onCancel={this.toggleModal}>
          <WarningBox message={translate('activationEditor.editRecurrenceWarning')} />
          <RecurrenceDatesSelector />
          <RestResultMessage id="backendError" result={this.state.backendError} />
        </ModalForm>
      </Modal>
    </React.Fragment>;
  }
}

function RecurrenceDates({type, dates}) {
  const dateTranslator = type === 'WEEKLY' ? showWeeklyRecurrenceDate : showRecurrenceDate;
  return <td>{R.join(', ',R.map(dateTranslator, dates))}</td>;
}

const showWeeklyRecurrenceDate = ({day}) => translateWeekday(day);
function showRecurrenceDate({day, month}) {
  if (day == LAST_DAY_OF_MONTH) return translate('evaluationActivator.lastWorkingDayOfMonth');
  let fullDate = day + '.' + month + '.';
  return month ? fullDate : day + '.';
}

class ActivatedParties extends React.Component {
  state = {
    backendError: null,
    selectedTypeIsInformant: false,
  };
  setSelectedPartyActivationType = (value) => {
    this.setState({
      selectedTypeIsInformant: value === partyActivationTypes.informant
    });
  };
  removeParty = (party) => {
    const {onChanged, activationId} = this.props;
    const isCurrentParty = getCurrentParty().id == party.id;
    //If removing yourself, ask for confirmation and proceed only if user confirms
    if (isCurrentParty && !window.confirm(translate("activationEditor.confirmOwnPartyRemoval"))) return;

    removeActivationParty(activationId, party.id).subscribe(
      () => {
        this.setState({
          backendError: null
        });
        onChanged();
      },
      (errorResponse) => this.setState({backendError: errorResponse})
    );
  };
  addParty = ({party, activationPartyType}, formApi) => {
    const {onChanged, activationId} = this.props;
    const addCommand = {
      id: party.id,
      activationType: activationPartyType
    };

    addActivationParty(activationId, addCommand).subscribe(
      () => {
        this.setState({ backendError: null });
        formApi.setValue("party", null);

        onChanged();
      },
      (errorResponse) => this.setState({backendError: errorResponse})
    );
  };
  render() {
    const {parties, isActive} = this.props;
    const sortedParties = R.sortWith([
      R.ascend(R.compose(translatePartyActivationType, R.prop('activationType'))),
      R.ascend(R.prop('firstname')),
      R.ascend(R.prop('lastname'))
    ], R.defaultTo([], parties));
    const canRemoveParties = parties.length > 1;
    const canEdit = isCompanyEmployee();

    return <section>
      <h2>{translate('activationEditor.responsibleParties')}</h2>
      <table id="responsible-parties-table" className="table table-striped">
        <tbody>
          {sortedParties.map(party => {
            return <tr key={party.id} data-party-id={party.id}>
              <td>{translatePartyActivationType(party.activationType)}</td>
              <td>{formatParty(party)}</td>
              <td className={party.email === null ? "alert-danger" : ""}>
                {party.email === null && translate("activationEditor.partyHasNoEmail")}
              </td>
              { isActive && canEdit ?
                  <td>
                    <Button id={"remove-party-"+party.id} className="remove-party-button" disabled={!canRemoveParties} size="xs"
                      buttonLook="danger" icon="remove" alt={translate("activationEditor.removeParty")}
                      onClick={() => this.removeParty(party)} />
                  </td> :
                  null
              }
            </tr>;
          })}
        </tbody>
      </table>
      { isActive && canEdit ?
          <FormWithState id="addParty" initialValues={{ activationPartyType: "SITE_COORDINATOR" }}
            onSubmit={this.addParty} >
            <h3>{translate('activationEditor.addParty')}</h3>
            <FlexRow flexAllChildren>
              <PartyActivationTypeSelector id="activationType" valuePath="activationPartyType"
              onChange={this.setSelectedPartyActivationType} />
              <ActivationPartySelector id="partyToAdd"
                useDebounce={true}
                valuePath="party" partyTypeValuePath="activationPartyType"
                excludedParties={parties} validate={validators.required} clearable={false}
                includeEmailOnlyUsers={this.state.selectedTypeIsInformant} />
              <SubmitButton flex="none" id="addPartyButton" text={translate('common.add')} />
            </FlexRow>
          </FormWithState> :
          null
      }
      <RestResultMessage id="partyModificationError" result={this.state.backendError} />
    </section>;
  }
}

function ActivationEventList(props) {
  const events = R.defaultTo([], props.events);
  const {onChanged} = props;
  const [sortState, setSortState] = useState({field: 'plannedDate', order: SortOrderEnum.ASCENDING});
  const [backendError, setBackendError] = useState();

  events.sort((a, b) => {
    // Assuming that all the sortable fields are strings (or nulls/undefined).
    const field = sortState.field;
    const valueA = a[field] || '';
    const valueB = b[field] || '';
    return valueA.localeCompare(valueB) * (sortState.order === SortOrderEnum.ASCENDING ? 1 : -1);
  });

  function onChangeSort(field, order) {
    setSortState({field: field, order: order});
  }

  function markSelfMonitoringDone(eventId) {
    submitSelfMonitoring(eventId).subscribe(
      () => {
        setBackendError();
        onChanged();
      },
      (errorResponse) => setBackendError(errorResponse)
    );
  }

  return <section>
    <h2>{translate('activationEditor.events')}</h2>
    <table id="events-table" className="table table-striped">
      <thead>
        <tr>
          <th className={styles.header}>{translate('common.edit')}</th>
          <th className={styles.header}><SortColumnHeader text="activationEditor.plannedDate"  field="plannedDate"  onChangeSort={onChangeSort}/></th>
          <th className={styles.header}><SortColumnHeader text="activationEditor.state"        field="state"        onChangeSort={onChangeSort}/></th>
          <th className={styles.header}><SortColumnHeader text="activationEditor.finishedDate" field="finishedDate" onChangeSort={onChangeSort}/></th>
          <th className={styles.header}>{translate('activationEditor.actions')}</th>
        </tr>
      </thead>
      <tbody>
        {events.map(event => {
          return <tr key={event.id} data-event-id={event.id}>
            <td>
              <EditButton className="edit-activation" link={"/event/"+event.id}
                canEdit={props.isActive}
                alt={translate('activationEditor.editActivation')} />
            </td>
            <td>{dates.format(event.plannedDate)}</td>
            <td className="event-state">{translateEventState(event.state)}</td>
            <td>{event.finishedDate ? dates.format(event.finishedDate) : "-"}</td>
            <td>
              {event.state === 'FINISHED' ?
                <DownloadPdfButton url={`/event/${event.id}/report/pdf`} text={translate('activationEditor.downloadPdfReport')}/>
                  :
                <React.Fragment>
                  { event.state === eventStates.selfMonitoring &&
                    <Button id="mark-self-monitoring-done" onClick={() => markSelfMonitoringDone(event.id)} disabled title={translate('activationEditor.phaseShouldBeDoneInMobile')}>
                      {translateEventAction('markSelfMonitoringDone')}
                    </Button>
                  }
                  { (event.state === eventStates.readyForEvaluation || event.state === eventStates.started) &&
                    <Button id="fill-evaluation" link={"/fill-evaluation/form/"+event.formId+"/event/"+event.id}>
                      {translateEventAction('fillEvaluation')}
                    </Button>
                  }
                  { event.state === eventStates.readyForCorrectionalRemarks &&
                    <Button id="fill-correctional-remarks" link={"/fill-evaluation/form/"+event.formId+"/event/"+event.id}>
                      {translateEventAction('fillCorrectionalRemarks')}
                    </Button>
                  }
                  { event.state === eventStates.readyForReview &&
                    <Button id="review-corrective-actions" link={"/review-evaluation/form/"+event.formId+"/event/"+event.id}>
                      {translateEventAction('reviewCorrectiveActions')}
                    </Button>
                  }
                </React.Fragment>
              }
            </td>
          </tr>;
        })}
        <tr>
          <td colSpan={5}>
            <RestResultMessage id="eventModificationError" result={backendError} />
          </td>
        </tr>
      </tbody>
    </table>
  </section>;
}

class CreateEventForm  extends React.Component {
  state = {
    backendError: null,
  };
  saveEvent = ({plannedDate}) => {
    const {onChanged, activationId} = this.props;
    const event = {
      activationId: activationId,
      plannedDate: dates.formatUTC(plannedDate),
    };

    createEvent(event).subscribe(onChanged, (errorResponse) => this.setState({backendError: errorResponse}));
  };
  render() {
    return <section>
      <FormWithState id="create-event" onSubmit={this.saveEvent}>
        <DatePicker id="event-date" valuePath='plannedDate' label="activationEditor.newEvent" validate={validators.required} />
        <SubmitButton className="pull-left" id="save-events" text={translate('common.create')} />
        <RestResultMessage id="createEventError" result={this.state.backendError} />
      </FormWithState>
    </section>;
  }
}



export default EvaluationActivationEditor;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/EvaluationActivationEditor.jsx